import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Tag, X, Plus, Search } from 'lucide-react';
import omnichannelService from '../../services/omnichannelService';

interface TagOption {
  id: string;
  name: string;
  color: string;
}

interface ConversationTagsProps {
  conversationId: string;
  currentTags?: string[];
  onTagsUpdated?: () => void;
}

export default function ConversationTags({
  conversationId,
  currentTags = [],
  onTagsUpdated
}: ConversationTagsProps) {
  const { theme } = useTheme();
  const { subscribeToOmnichannel } = useNotifications();
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(currentTags);
  const [showTagSelector, setShowTagSelector] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3B82F6');
  const [creating, setCreating] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAvailableTags();
  }, []);

  useEffect(() => {
    setSelectedTags(currentTags);
  }, [currentTags]);

  // Listen for real-time tag updates
  useEffect(() => {
    const unsubscribe = subscribeToOmnichannel((event) => {
      if (event.type === 'tags_updated' && event.data.conversation_id === conversationId) {
        if (event.data.tags) {
          setSelectedTags(event.data.tags);
        }
        onTagsUpdated?.();
      }
    });

    return () => unsubscribe();
  }, [conversationId, subscribeToOmnichannel, onTagsUpdated]);

  const loadAvailableTags = async () => {
    try {
      const response = await omnichannelService.getAllTags();
      setAvailableTags(response || []);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleAddTag = async (tagId: string) => {
    try {
      setLoading(true);
      await omnichannelService.addTags(conversationId, [tagId]);
      setSelectedTags([...selectedTags, tagId]);
      setShowTagSelector(false);
      setSearchQuery('');
      onTagsUpdated?.();
    } catch (error) {
      console.error('Failed to add tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      setLoading(true);
      await omnichannelService.removeTags(conversationId, tagId);
      setSelectedTags(selectedTags.filter((id) => id !== tagId));
      onTagsUpdated?.();
    } catch (error) {
      console.error('Failed to remove tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      setCreating(true);
      const newTag = await omnichannelService.createTag(newTagName.trim(), newTagColor);

      // Reload tags to get the new one
      await loadAvailableTags();

      // Auto-assign the newly created tag
      await handleAddTag(newTag.id);

      // Reset form
      setNewTagName('');
      setNewTagColor('#3B82F6');
      setShowCreateForm(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setCreating(false);
    }
  };

  const getTagColor = (color: string) => {
    // Calculate luminance to determine if text should be light or dark
    const getLuminance = (hex: string) => {
      const rgb = parseInt(hex.slice(1), 16);
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = (rgb >> 0) & 0xff;
      return 0.299 * r + 0.587 * g + 0.114 * b;
    };

    const luminance = getLuminance(color);
    const textColor = luminance > 128 ? '#000000' : '#FFFFFF';

    return { backgroundColor: color, color: textColor };
  };

  const getSelectedTagObjects = () => {
    return availableTags.filter((tag) => selectedTags.includes(tag.id));
  };

  const getUnselectedTags = () => {
    return availableTags.filter((tag) => !selectedTags.includes(tag.id));
  };

  const getFilteredTags = () => {
    const unselected = getUnselectedTags();
    if (!searchQuery.trim()) return unselected;

    return unselected.filter((tag) => tag.name.toLowerCase().includes(searchQuery.toLowerCase()));
  };

  const exactMatchExists = () => {
    return availableTags.some((tag) => tag.name.toLowerCase() === searchQuery.trim().toLowerCase());
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4
          className={`text-sm font-semibold uppercase ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
        >
          Tags
        </h4>
        <button
          onClick={() => {
            setShowTagSelector(!showTagSelector);
            if (!showTagSelector) {
              setTimeout(() => searchInputRef.current?.focus(), 100);
            }
          }}
          className={`p-1 rounded transition-colors ${
            theme === 'dark' ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
          }`}
          disabled={loading}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2">
        {getSelectedTagObjects().map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
            style={getTagColor(tag.color)}
          >
            <Tag className="h-3 w-3" />
            {tag.name}
            <button
              onClick={() => handleRemoveTag(tag.id)}
              className="ml-1 hover:opacity-70"
              disabled={loading}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {selectedTags.length === 0 && (
          <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            No tags added
          </span>
        )}
      </div>

      {/* Tag Selector with Search and Create */}
      {showTagSelector && (
        <div
          className={`p-3 rounded-lg border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
        >
          {/* Search Input */}
          <div className="relative mb-3">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search or create tag..."
              className={`w-full pl-10 pr-3 py-2 rounded-lg text-sm ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-400'
                  : 'bg-white text-gray-900 border-gray-300 placeholder-gray-500'
              } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Existing Tags List */}
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {getFilteredTags().map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleAddTag(tag.id)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-200'
                    : 'hover:bg-gray-100 text-gray-800'
                }`}
                disabled={loading}
              >
                <span
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs"
                  style={getTagColor(tag.color)}
                >
                  <Tag className="h-3 w-3" />
                  {tag.name}
                </span>
              </button>
            ))}

            {/* No Results */}
            {getFilteredTags().length === 0 && !searchQuery.trim() && (
              <p
                className={`text-xs text-center py-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}
              >
                All tags have been added
              </p>
            )}
          </div>

          {/* Create New Tag */}
          {searchQuery.trim() && !exactMatchExists() && (
            <div
              className={`mt-3 pt-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}
            >
              {!showCreateForm ? (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-blue-400'
                      : 'hover:bg-gray-100 text-blue-600'
                  }`}
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Create "{searchQuery}"
                </button>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Tag Name
                    </label>
                    <input
                      type="text"
                      value={newTagName || searchQuery}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Enter tag name"
                      className={`w-full px-3 py-2 rounded-lg text-sm ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-200 border-gray-600'
                          : 'bg-white text-gray-900 border-gray-300'
                      } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-xs font-medium mb-1 ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Color
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={newTagColor}
                        onChange={(e) => setNewTagColor(e.target.value)}
                        className="h-10 w-16 rounded cursor-pointer"
                      />
                      <span
                        className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                      >
                        {newTagColor}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateTag}
                      disabled={creating || !newTagName.trim()}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        creating || !newTagName.trim()
                          ? theme === 'dark'
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : theme === 'dark'
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {creating ? 'Creating...' : 'Create & Add'}
                    </button>
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewTagName('');
                        setNewTagColor('#3B82F6');
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        theme === 'dark'
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
